// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@tps/App/Components/Common/XngZone";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { constants, DivWrapper } from "@tps/XDL/constants";
import { color, xScreen } from "@tps/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

interface Props extends WidgetProps {
    fields: {
        rowCount: number;
        gap: number;
        mobileGap: number;
        margin: string;
        overrideStyle: boolean;
        itemsList: {
            fields: {
                name: string;
                removeFooterOffset: boolean;
            };
        }[];
    };
}

const styles: { container?: InjectableCss; itemContainer?: InjectableCss } = {
    container: {
        css: css`
            max-width: 100%;
            ${({ margin }: any) => margin && `margin: ${margin};`}
            > div {
                margin-bottom: ${({ gap }: any) => gap}px;
                ${xScreen("lte", "md")} {
                    margin-bottom: ${({ gap }: any) => gap}px;
                }
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    itemContainer: {
        css: css`
            width: 100%;

            ${({ removeFooterOffset, isInShell }: any) => css`
                ${removeFooterOffset && !isInShell &&
                css`
                    &&&& {
                        margin-bottom: -${constants.footer.offsetTop}px;
                    }
                `}
            `}
        `,
    },
};

// A widget that makes a configurable number of row zones within its container.
const Rows: React.FunctionComponent<Props> = ({
    fields: { margin, rowCount, gap, overrideStyle, itemsList, mobileGap },
    id,
}: Props) => {
    const rows: JSX.Element[] = [];
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);
    const { isInShell } = useShellContext();

    if (overrideStyle) {
        itemsList?.forEach((item, index) => {
            const { removeFooterOffset, name: itemName = "" } = item.fields;
            const name = itemName.replace(/\s/g, "");

            const itemProps = {
                removeFooterOffset: removeFooterOffset && index === itemsList.length - 1,
                isInShell,
            };

            rows.push(
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper key={`StyledWrapper-${id}-${name || index}`} {...itemProps} {...styles.itemContainer}>
                    <XngZone contentId={id} zoneName={`Content_${name || index}`} hint={`Zone for ${itemName}`} />
                </StyledWrapper>,
            );
        });
    } else {
        for (let row = 0; row < rowCount; row = row + 1) {
            rows.push(
                <GridItem key={`GridItem-${id}-${row}`} width={[12, 12, 12, 12, 12]}>
                    <Zone contentId={id} zoneName={`Content${row}`} />
                </GridItem>,
            );
        }
    }

    if (overrideStyle) {
        return (
            <StyledWrapperX gap={gap} margin={margin} mobileGap={mobileGap} {...styles.container}>
                {rows}
            </StyledWrapperX>
        );
    }

    return (
        <GridContainer
            gap={gap}
            css={css`
                ${margin &&
                css`
                    margin: ${margin};
                `}
            `}
        >
            {rows}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: Rows,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: "rowCount",
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                isVisible: item => !item.fields.overrideStyle,
            },
            {
                name: "gap",
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            {
                name: "mobileGap",
                displayName: "Gap Between Items on Mobile",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            {
                name: "margin",
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
            },
            {
                name: "overrideStyle",
                displayName: "Override Style",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Zone";
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.overrideStyle,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Zone Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Zone Name",
                        isRequired: true,
                    },
                    {
                        name: "removeFooterOffset",
                        displayName: "Remove Footer Offset",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "Use it to remove footer offset, It'll apply for the last item only",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
